<template>
  <div class="accruals-page" data-testid="accruals-page">
    <accruals-brand-loader v-if="!setupGateReady" />
    <template v-else>
    <header class="accruals-page__header">
      <div class="accruals-page__title-group">
        <h1 class="accruals-page__title">Accruals</h1>
        <v-tooltip bottom content-class="tooltip-bottom">
          <template #activator="{ on, attrs }">
            <base-button
              v-bind="attrs"
              icon-style
              small
              class="shadow-off accruals-page__refresh-btn"
              icon="mdi-refresh"
              :loading="headerRefreshLoading"
              :disabled="headerRefreshLoading"
              aria-label="Refresh accruals"
              data-testid="accruals-page__refresh-btn"
              v-on="on"
              @click="onHeaderRefresh"
            />
          </template>
          <span>Refresh</span>
        </v-tooltip>
      </div>
    </header>

    <v-alert
      v-if="error"
      type="error"
      dense
      text
      class="accruals-page__alert"
      data-testid="accruals-page__error"
    >
      {{ error }}
    </v-alert>

    <div
      class="accruals-page__body"
      :class="{ 'accruals-page__body--loading': refreshLoading }"
    >
      <accruals-hero-banner
        :info-request-configured="infoRequestConfigured"
        @run-estimates="onRunEstimates"
      />

      <div class="accruals-page__cards">
        <accruals-configuration-card
          :project-id="projectId"
          :estimation-rules-count="estimationRulesCount"
          :info-request-rules-count="infoRequestRulesCount"
        />
        <accruals-data-card
          :metrics="metrics"
          :last-synced-label="lastSyncedLabel"
          :issue-label="ingestIssueLabel"
          @open="openDataPage()"
          @open-metric="openDataPageForMetric"
          @sync="onSync"
        />
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import AccrualsBrandLoader from './components/AccrualsBrandLoader.vue';
import AccrualsHeroBanner from './components/AccrualsHeroBanner.vue';
import AccrualsConfigurationCard from './components/AccrualsConfigurationCard.vue';
import AccrualsDataCard from './components/AccrualsDataCard.vue';
import { getDataTabIdFromMetricKey } from './accrualsDataConstants';
import { ACCRUALS_ASSETS, ACCRUALS_METRIC_KEYS, ACCRUALS_ROUTES } from './accrualsPageConstants';
import {
  countInfoRequestRulesConfigured,
  isInfoRequestRuleConfigured,
} from './accrualsConfigurationStorage';

const METRIC_LABELS = {
  [ACCRUALS_METRIC_KEYS.OPEN_PO]: 'Open POs',
  [ACCRUALS_METRIC_KEYS.AP_POSTINGS]: 'AP records',
  [ACCRUALS_METRIC_KEYS.ACCRUALS_LEDGER]: 'Accrual ledger',
  [ACCRUALS_METRIC_KEYS.VENDOR_MASTER]: 'Vendors',
};

export default {
  name: 'Accruals',
  components: {
    AccrualsBrandLoader,
    AccrualsHeroBanner,
    AccrualsConfigurationCard,
    AccrualsDataCard,
  },
  data() {
    return {
      assets: ACCRUALS_ASSETS,
      estimationConfigured: false,
      infoRequestConfigured: false,
      estimationRulesCount: 0,
      infoRequestRulesCount: 0,
      rulesCount: 0,
      refreshLoading: false,
      setupGateReady: false,
    };
  },
  computed: {
    accrualsState() {
      return this.$store.state.m_financialclose?.accruals || {};
    },
    summary() {
      return this.accrualsState.summary || {};
    },
    flowStatus() {
      return this.accrualsState.flowStatus || {};
    },
    headerRefreshLoading() {
      return this.refreshLoading;
    },
    error() {
      return this.accrualsState.error;
    },
    projectId() {
      return this.$route.params.id;
    },
    cid() {
      return this.$route.query.cid || this.$store.state.client;
    },
    entitySummary() {
      return this.summary?.entities || {};
    },
    metrics() {
      return Object.keys(METRIC_LABELS).map((key) => ({
        key,
        label: METRIC_LABELS[key],
        value: this.formatCount(this.entitySummary[key]?.canonical_count),
      }));
    },
    lastSyncedLabel() {
      const timestamps = Object.values(this.entitySummary || {})
        .filter((entity) => {
          const count = Number(entity?.canonical_count) || 0;
          if (entity?.ingest?.outcome === 'failed' && !count) return false;
          return (count > 0 || entity?.active_version_id) && entity?.last_synced_at;
        })
        .map((entity) => new Date(entity.last_synced_at).getTime())
        .filter((value) => !Number.isNaN(value));
      if (!timestamps.length) return '';
      const latest = Math.max(...timestamps);
      return this.$nova.formatDateTime(new Date(latest));
    },
    ingestIssueLabel() {
      const entities = Object.values(this.entitySummary || {});
      const failed = entities.find(
        (entity) => entity?.ingest?.outcome === 'failed' && entity?.ingest?.message
      );
      if (failed) return failed.ingest.message;
      const partial = entities.find(
        (entity) => entity?.ingest?.outcome === 'completed_with_rejects' && entity?.ingest?.message
      );
      return partial?.ingest?.message || '';
    },
    hasSourceData() {
      const keys = [
        ACCRUALS_METRIC_KEYS.OPEN_PO,
        ACCRUALS_METRIC_KEYS.AP_POSTINGS,
        ACCRUALS_METRIC_KEYS.VENDOR_MASTER,
      ];
      return keys.some((key) => Number(this.entitySummary[key]?.canonical_count) > 0);
    },
    hasConfiguredRules() {
      return this.estimationConfigured || this.infoRequestConfigured;
    },
  },
  activated() {
    if (!this.setupGateReady) return;
    this.refreshPageData({ showSetupGate: false });
  },
  created() {
    this.refreshPageData({ showSetupGate: true });
  },
  beforeDestroy() {
    this.$store.commit('accruals/setError', null);
    this.estimationConfigured = false;
    this.infoRequestConfigured = false;
    this.estimationRulesCount = 0;
    this.infoRequestRulesCount = 0;
    this.rulesCount = 0;
    this.setupGateReady = false;
  },
  methods: {
    formatCount(value) {
      const count = Number(value) || 0;
      return this.$nova.formatNumber ? this.$nova.formatNumber(count) : String(count);
    },
    async refreshPageData({ showSetupGate = false } = {}) {
      if (showSetupGate) {
        this.setupGateReady = false;
      }
      try {
        await Promise.all([
          this.refreshRulesStatus(),
          this.$store.dispatch('accruals/loadSummary', {
            projectId: this.projectId,
            cid: this.cid,
          }),
          this.$store.dispatch('accruals/loadFlowStatus', {
            projectId: this.projectId,
            cid: this.cid,
          }),
        ]);
      } catch (err) {
        console.log('[Accruals]: refreshPageData failed', err);
      } finally {
        if (this.shouldStayOnSetupPage()) {
          this.setupGateReady = true;
        } else {
          console.log('[Accruals]:', {
            action: 'redirect-workbench',
            hasSourceData: this.hasSourceData,
            hasConfiguredRules: this.hasConfiguredRules,
          });
          this.$router.replace(
            this.$nova.gotoLink({ path: ACCRUALS_ROUTES.estimates(this.projectId) }),
          );
        }
      }
    },
    shouldStayOnSetupPage() {
      return !this.hasSourceData && !this.hasConfiguredRules;
    },
    async refreshRulesStatus() {
      let estimationCount = 0;
      try {
        const rules = await this.$store.dispatch('accruals/loadComputationRules', {
          projectId: this.projectId,
        });
        estimationCount = (rules || []).filter((rule) => rule.status !== 'disabled').length;
      } catch (err) {
        console.log('[Accruals]: loadComputationRules failed', err);
        estimationCount = 0;
      }
      this.estimationConfigured = estimationCount > 0;
      this.estimationRulesCount = estimationCount;

      let infoCount = 0;
      try {
        const imRule = await this.$store.dispatch('accruals/loadImRule', {
          projectId: this.projectId,
        });
        this.infoRequestConfigured = isInfoRequestRuleConfigured(imRule);
        infoCount = countInfoRequestRulesConfigured(imRule);
        this.infoRequestRulesCount = infoCount;
      } catch (err) {
        console.log('[Accruals]: loadImRule failed', err);
        this.infoRequestConfigured = false;
        this.infoRequestRulesCount = 0;
      }

      this.rulesCount = estimationCount + infoCount;
    },
    async onHeaderRefresh() {
      if (this.headerRefreshLoading || !this.projectId) return;
      console.log('[Accruals]:', { action: 'refresh', projectId: this.projectId });
      this.refreshLoading = true;
      try {
        await this.refreshPageData({ showSetupGate: false });
        this.$store.commit('sbSuccess', 'Accruals refreshed', { root: true });
      } catch (err) {
        console.error('[Accruals]: onHeaderRefresh error', err);
      } finally {
        this.refreshLoading = false;
      }
    },
    async onSync() {
      this.refreshLoading = true;
      try {
        await this.refreshPageData({ showSetupGate: false });
        this.$store.commit(
          'sbSuccess',
          'Accruals data refreshed',
          { root: true },
        );
      } finally {
        this.refreshLoading = false;
      }
    },
    openDataPage(tabId) {
      const path = ACCRUALS_ROUTES.data(this.projectId);
      const addquery = tabId ? { tab: tabId } : {};
      this.$router.push(this.$nova.gotoLink({ path, addquery }));
    },
    openDataPageForMetric(metricKey) {
      const tabId = getDataTabIdFromMetricKey(metricKey);
      this.openDataPage(tabId);
    },
    onRunEstimates() {
      if (!this.infoRequestConfigured) return;
      const path = ACCRUALS_ROUTES.estimates(this.projectId);
      this.$router.push(this.$nova.gotoLink({ path }));
    },
  },
};
</script>

<style lang="scss" scoped>
@import './Accruals.scss';
</style>
